import type { StockyPOJobState } from '@/models/stocky_migration';

/**
 * Presentation logic for the Stocky PO import job lifecycle.
 *
 * Kept out of the .vue file so it can be tested directly rather than through a
 * mirrored copy of the component.
 *
 * The distinction this module exists to draw: a job that is *queued* and a job
 * that is *running* look identical in the raw status payload (progress 0, no
 * phase worth showing), but they mean very different things to a merchant. A
 * queued job is waiting on a worker and there is nothing to watch; a running one
 * is making progress. Showing a 0% ring for the first is what made a normal
 * queue wait look like a broken import.
 */

/** How long a job may sit queued before we say so out loud. */
export const QUEUE_SLOW_AFTER_MS = 90 * 1000;

/**
 * Past this, a queue wait is no longer "busy" but "something is probably wrong"
 * — long enough that the usual cause is no worker consuming the queue rather
 * than a backlog. The copy switches to telling the merchant it is safe to leave
 * and who to contact, instead of implying they should keep waiting.
 */
export const QUEUE_STALLED_AFTER_MS = 5 * 60 * 1000;

export type QueueSeverity = 'normal' | 'slow' | 'stalled';

export interface QueueWaitCopy {
  headline: string;
  detail: string;
  severity: QueueSeverity;
}

/** Enqueued, but no worker has picked it up yet. */
export function isQueued(status: StockyPOJobState | undefined | null): boolean {
  return status === 'created';
}

/** A worker has it and is reporting progress. */
export function isRunning(status: StockyPOJobState | undefined | null): boolean {
  return status === 'processing';
}

/** Nothing further will happen — stop polling. */
export function isTerminal(status: StockyPOJobState | undefined | null): boolean {
  return status === 'completed' || status === 'error' || status === 'not_found';
}

/**
 * Terminal, with no usable results — the merchant has to run a new import.
 *
 * `not_found` belongs here alongside `error` even though it is not strictly a
 * failure of the import: the job record is gone, so there is nothing to show and
 * nothing to carry into the next step. Wizards that only checked for `error`
 * left the Step 3 action live, which either advanced past an import that never
 * produced results or rendered a button that did nothing at all.
 */
export function isFailed(status: StockyPOJobState | undefined | null): boolean {
  return status === 'error' || status === 'not_found';
}

/**
 * Round a wait down to a unit a person would use out loud. Deliberately coarse:
 * a live-updating second counter invites the merchant to watch the screen, which
 * is the opposite of what we want them to do while a job is queued.
 */
export function formatWait(elapsedMs: number): string {
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000));
  if (seconds < 60) {
    return 'a few seconds';
  }
  const minutes = Math.floor(seconds / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

/**
 * What to tell the merchant while the job waits for a worker, escalating with
 * the wait. Every variant states the same two reassurances — the import is
 * queued, and closing the window will not cancel it — because the fear at this
 * point is that nothing was submitted.
 */
export function queueWaitCopy(elapsedMs: number): QueueWaitCopy {
  if (elapsedMs >= QUEUE_STALLED_AFTER_MS) {
    return {
      severity: 'stalled',
      headline: 'Still waiting to start',
      detail:
        `Your import has been queued for ${formatWait(elapsedMs)}, which is longer than usual. `
        + 'It is safe to close this window — the import will run when a worker frees up, and '
        + 'your purchase orders are not affected. If it has not finished in an hour, contact '
        + 'support via the chat widget and mention the import.',
    };
  }

  if (elapsedMs >= QUEUE_SLOW_AFTER_MS) {
    return {
      severity: 'slow',
      headline: 'Waiting for an available worker',
      detail:
        `Queued for ${formatWait(elapsedMs)}. Other imports are running ahead of yours. `
        + 'You can leave this page — the import will continue on its own.',
    };
  }

  return {
    severity: 'normal',
    headline: 'Waiting to start',
    detail:
      'Your import is queued and will begin as soon as a worker picks it up. '
      + 'You can leave this page — it will keep running.',
  };
}

/** Vuetify colour for the queue-wait card, matching the severity. */
export function queueWaitColor(severity: QueueSeverity): string {
  if (severity === 'stalled') {
    return 'orange';
  }
  return severity === 'slow' ? 'blue-grey' : 'primary';
}
